import React, { FC, useMemo, useRef } from 'react';

import { Button, Center, Flex, Heading, Stack, Text } from '@chakra-ui/react';
import { HiDocumentAdd } from 'react-icons/hi';
import { useQuery } from 'react-query';

import { fetchManufacturers } from 'api/manufacturer';
import { UserPill } from 'components/core/UserPill';
import { DataTable } from 'components/modules/DataTable';
import {
  ManufacturerModal,
  ManufacturerModalRef,
} from 'components/modules/ManufacturerModal';
import { useRoles } from 'store/slices/auth/authSelectors';
import { Manufacturer } from 'types/manufacturer';
import { RolesEnum } from 'types/user';

import { buildManufacturerColumns } from './manufacturersColumns';

/**
 * Inventory → Manufacturers (ClickUp 86bceudwp): every manufacturer with its
 * emails and address. Super admins can add one, or click a row to edit it.
 */
const Manufacturers: FC = () => {
  const modalRef = useRef<ManufacturerModalRef>(null);

  const roles = useRoles();
  const isSuperAdmin = Boolean(
    roles?.some((permission) => permission.role === RolesEnum.SUPER_ADMIN),
  );

  // A short list, sorted by name on the API, shown in full.
  const {
    data = [],
    isLoading,
    isFetching,
  } = useQuery('fetch-manufacturers-table', () => fetchManufacturers());

  const columns = useMemo(() => buildManufacturerColumns(), []);

  return (
    <Flex direction="column" flex={1} color="support.500" overflow="auto">
      <Flex alignItems="baseline" justify="space-between">
        <Heading fontSize="24px">Manufacturers</Heading>
        <Stack isInline>
          {isSuperAdmin && (
            <Button
              w="220px"
              leftIcon={<HiDocumentAdd size="20px" />}
              onClick={() => modalRef.current?.onOpen()}>
              Add Manufacturer
            </Button>
          )}
          <UserPill />
        </Stack>
      </Flex>
      <Flex h="40px" />
      <DataTable<Manufacturer>
        minW="720px"
        data={data}
        columns={columns}
        isLoading={isLoading || isFetching}
        onRowClick={
          isSuperAdmin
            ? (manufacturer) =>
                manufacturer && modalRef.current?.onOpen(manufacturer)
            : undefined
        }
        renderEmpty={() => (
          <Center p={8}>
            <Text fontSize="20px">No manufacturers yet</Text>
          </Center>
        )}
      />
      {isSuperAdmin && <ManufacturerModal ref={modalRef} />}
    </Flex>
  );
};

export default Manufacturers;
